import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Id } from "@/jenesys-types/data/common";
import { AppError } from "@/jenesys-types/errors";
import { Transaction } from "@/jenesys-types/jenesys-entities/config";

export interface TransactionPublishState {
  isPublishing: boolean;
  publishingDocumentId?: string;
  error?: AppError;
  isUpdatingStatus: boolean;
  updatingStatusDocumentId?: string;
  statusUpdateError?: AppError;
  isManualPublishing: boolean;
}

export interface PublishTransactionPayload {
  documentId: string;
  companyId: Id;
  transaction: Transaction;
}

export interface UpdateDocumentStatusPayload {
  documentId: string;
  companyId: string;
  status: string;
  published_manually?: boolean;
}

export type PublishTransactionAction = PayloadAction<PublishTransactionPayload>;
export type PublishTransactionSuccessAction = PayloadAction<{
  documentId: string;
  transaction: Transaction;
}>;
export type PublishTransactionFailureAction = PayloadAction<{
  documentId: string;
  error: AppError;
}>;

export type UpdateDocumentStatusAction = PayloadAction<UpdateDocumentStatusPayload>;
export type UpdateDocumentStatusSuccessAction = PayloadAction<{
  documentId: string;
  status: string;
}>;
export type UpdateDocumentStatusFailureAction = PayloadAction<{
  documentId: string;
  error: AppError;
}>;

const initialState: TransactionPublishState = {
  isPublishing: false,
  isUpdatingStatus: false,
  isManualPublishing: false
};

const name = "features/transactions/publish";

const publishSlice = createSlice({
  name,
  initialState,
  reducers: {
    publishTransaction: {
      reducer: (
        state: TransactionPublishState,
        action: PublishTransactionAction
      ) => {
        state.isPublishing = true;
        state.publishingDocumentId = action.payload.documentId;
        state.error = undefined;
      },
      prepare: (
        documentId: string,
        companyId: Id,
        transaction: Transaction
      ) => ({
        payload: { documentId, companyId, transaction }
      })
    },
    publishTransactionSuccess: {
      reducer: (
        state: TransactionPublishState,
        _action: PublishTransactionSuccessAction
      ) => {
        state.isPublishing = false;
        state.publishingDocumentId = undefined;
      },
      prepare: (documentId: string, transaction: Transaction) => ({
        payload: { documentId, transaction }
      })
    },
    publishTransactionFailure: {
      reducer: (
        state: TransactionPublishState,
        action: PublishTransactionFailureAction
      ) => {
        state.isPublishing = false;
        state.publishingDocumentId = undefined;
        state.error = action.payload.error;
      },
      prepare: (documentId: string, error: AppError) => ({
        payload: { documentId, error }
      })
    },
    updateDocumentStatusRequest: {
      reducer: (
        state: TransactionPublishState,
        action: UpdateDocumentStatusAction
      ) => {
        state.isUpdatingStatus = true;
        state.updatingStatusDocumentId = action.payload.documentId;
        state.statusUpdateError = undefined;
      },
      prepare: (
        documentId: string,
        companyId: string,
        status: string,
        published_manually?: boolean
      ) => ({
        payload: { documentId, companyId, status, published_manually }
      })
    },
    updateDocumentStatusSuccess: {
      reducer: (
        state: TransactionPublishState,
        _action: UpdateDocumentStatusSuccessAction
      ) => {
        state.isUpdatingStatus = false;
        state.updatingStatusDocumentId = undefined;
      },
      prepare: (documentId: string, status: string) => ({
        payload: { documentId, status }
      })
    },
    updateDocumentStatusFailure: {
      reducer: (
        state: TransactionPublishState,
        action: UpdateDocumentStatusFailureAction
      ) => {
        state.isUpdatingStatus = false;
        state.updatingStatusDocumentId = undefined;
        state.statusUpdateError = action.payload.error;
      },
      prepare: (documentId: string, error: AppError) => ({
        payload: { documentId, error }
      })
    },
    clearPublishError: (state: TransactionPublishState) => {
      state.error = undefined;
    }
  }
});

// Export actions
export const {
  publishTransaction,
  publishTransactionSuccess,
  publishTransactionFailure,
  clearPublishError,
  updateDocumentStatusRequest,
  updateDocumentStatusSuccess,
  updateDocumentStatusFailure
} = publishSlice.actions;

// Selectors
export const selectIsPublishingTransaction = (state: {
  features?: { transactions?: { publish?: TransactionPublishState } };
}) => state.features?.transactions?.publish?.isPublishing ?? false;

export const selectPublishError = (state: {
  features?: { transactions?: { publish?: TransactionPublishState } };
}) => state.features?.transactions?.publish?.error;

// Export reducer
export default publishSlice.reducer;
